/* @flow */

import clsx from 'clsx';
import * as React from 'react';
import { useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Messenger, MessengerEvents } from '../../helpers/messenger';
import { AvenueType } from '../../helpers/ui/avenue/types';
import { navigateToAvenue } from '../../redux/ui/actions';
import './Item.css';
import { NO_AVENUE_INDEX } from './NavigationMenu';

export type MenuItemHandle = {
  getTotalWidth: () => number,
};

type MenuItemPropType = {
  +index: number,
  +onSelect?: () => void,
  +text: string,
  +type: AvenueType,
};

const MenuItem = ({ index, onSelect, text, type }: MenuItemPropType, ref: React.RefSetter<MenuItemHandle>): React.Node => {
  const dispatch = useDispatch();

  const focusedAvenueIndex = useSelector<number>((state) => state.ui.navigationStack[state.ui.navigationStack.length - 1]?.index ?? NO_AVENUE_INDEX);

  const itemElement = useRef<HTMLElement | null>(null);
  const [totalWidth, setTotalWidth] = useState<number | null>(null);

  const getTotalWidth = useCallback((): number => {
    const itemElt = itemElement.current;

    if (!itemElt) {
      return 0;
    }

    if (totalWidth) {
      return totalWidth;
    }

    const { marginLeft, marginRight } = window.getComputedStyle(itemElt);
    const newTotalWidth = Math.ceil(itemElt.offsetWidth) + Math.ceil(parseFloat(marginLeft)) + Math.ceil(parseFloat(marginRight));
    setTotalWidth(newTotalWidth);

    return newTotalWidth;
  }, [totalWidth]);

  useImperativeHandle(ref, (): MenuItemHandle => {
    return {
      getTotalWidth,
    };
  });

  const handleOnClick = useCallback(
    (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement> | SyntheticKeyboardEvent<HTMLElement>) => {
      // Stop propagation to prevent AccessibleButton (.burgerIcon) from catching the event
      event.stopPropagation();

      if (typeof onSelect === 'function') {
        onSelect();
      }

      if (type === AvenueType.Explore) {
        Messenger.emit(MessengerEvents.OPEN_EXPLORE_MODAL, index);
        return;
      }

      if (index === focusedAvenueIndex) {
        // Focused menu item has been clicked again: reset avenue
        Messenger.emit(MessengerEvents.AVENUE_RESET);
        return;
      }

      // Update focused avenue in store (which will trigger the actual loading of the avenue)
      dispatch(navigateToAvenue(index, type));
    },
    [dispatch, focusedAvenueIndex, index, onSelect, type],
  );

  const handleKeyDown = useCallback((event: SyntheticKeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      // Prevent keydown from bubbling up to AccessibleButton (.burgerIcon)
      event.stopPropagation();
    }
  }, []);

  return (
    <li
      className={clsx('avenueElement', index === focusedAvenueIndex && 'focused', (type === AvenueType.MyVideos || type === AvenueType.Explore) && 'separator')}
      ref={itemElement}
      role='presentation'
    >
      <button className='text' data-text={text} onClick={handleOnClick} onKeyDown={handleKeyDown} type='button'>
        {text}
      </button>
    </li>
  );
};

export default React.forwardRef<MenuItemPropType, MenuItemHandle>(MenuItem) as React.ComponentType<{
  ...MenuItemPropType,
  +ref?: React.RefSetter<MenuItemHandle> | { current: MenuItemHandle | null, ... } | null,
}>;
